/**
 * DeleteCreatureButton Component
 *
 * A button for deleting creatures with confirmation modal.
 * Features:
 * - Loading states during API calls
 * - Confirmation modal before deletion
 * - Visual feedback and tooltips
 * - Full accessibility support
 */

import React, { useState } from 'react';
import { getCreatureLibraryService } from '../services/creatureLibraryService';
import { ConfirmationModal } from './ConfirmationModal';
import './DeleteCreatureButton.css';

export interface DeleteCreatureButtonProps {
  /** The ID of the creature to delete */
  creatureId: string;
  /** The name of the creature (for display in confirmation) */
  creatureName: string;
  /** Whether the button should be disabled */
  disabled?: boolean;
  /** Callback fired when the delete completes (success or failure) */
  onDeleteComplete?: (success: boolean, message: string) => void;
}

/**
 * Trash icon SVG
 */
const TrashIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <line x1="10" y1="11" x2="10" y2="17" />
    <line x1="14" y1="11" x2="14" y2="17" />
  </svg>
);

/**
 * Loading spinner SVG for loading state
 */
const LoadingSpinner: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className="loading-spinner"
  >
    <circle
      cx="12"
      cy="12"
      r="10"
      fill="none"
      strokeWidth="3"
      stroke="currentColor"
      strokeLinecap="round"
      strokeDasharray="31.416"
      strokeDashoffset="10"
    />
  </svg>
);

export const DeleteCreatureButton: React.FC<DeleteCreatureButtonProps> = ({
  creatureId,
  creatureName,
  disabled = false,
  onDeleteComplete,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleButtonClick = () => {
    if (disabled || isLoading) return;
    setShowConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    setIsLoading(true);
    try {
      const libraryService = getCreatureLibraryService();
      await libraryService.deleteCreature(creatureId);
      setShowConfirmation(false);
      onDeleteComplete?.(true, 'Creature deleted successfully');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete creature';
      onDeleteComplete?.(false, message);
      // Keep modal open on error so user can try again or cancel
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelDelete = () => {
    if (!isLoading) {
      setShowConfirmation(false);
    }
  };

  const isDisabled = disabled || isLoading;
  const label = 'Delete creature';

  return (
    <>
      <button
        type="button"
        className={`delete-creature-button ${isLoading ? 'loading' : ''}`}
        onClick={handleButtonClick}
        disabled={isDisabled}
        aria-label={label}
        title={label}
      >
        {isLoading ? <LoadingSpinner /> : <TrashIcon />}
      </button>

      <ConfirmationModal
        isOpen={showConfirmation}
        title="Delete Creature"
        message={`Are you sure you want to delete "${creatureName}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isDangerous={true}
        isLoading={isLoading}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </>
  );
};

export default DeleteCreatureButton;
